import styled from '@emotion/styled'
import { useMantineTheme } from '@mantine/core'
import { useDayjs } from 'hooks/useDayjs'
import { CSSProperties, MouseEventHandler } from 'react'
import { useNavigate } from 'react-router-dom'
import { TDatatable } from '.'
import classes from './DatatableRow.module.css'

const hasName = (value: unknown): value is { name: string } => {
  return typeof value === 'object' && value !== null && 'name' in value
}

export type DatatableRowProps<TItem> = {
  headers?: TDatatable<TItem>[]
  exclude?: string[]
  href?: (_data: TItem) => string
  onClick?: (_data: TItem) => void
  value: TItem
} & Partial<Omit<HTMLTableRowElement, 'children'>>

export const DatatableRow = <TItem,>({
  value,
  headers,
  href,
  onClick,
  className,
}: DatatableRowProps<TItem>) => {
  const navigate = useNavigate()
  const dayjs = useDayjs()
  const theme = useMantineTheme()
  const hasClickEvent = !!href || !!onClick
  const rowStyle = {
    '--datatable-row-border-color': 'var(--cl-app-border)',
    '--datatable-row-background': 'var(--cl-app-surface-elevated)',
    '--datatable-row-radius': theme.radius.md,
    '--datatable-row-cursor': hasClickEvent ? 'pointer' : 'default',
    '--datatable-row-hover-background': 'var(--cl-app-surface-subtle)',
    '--datatable-row-label-color': 'var(--cl-app-text-muted)',
  } as CSSProperties

  if (!headers) throw new Error('No datatable headers present')

  const handleRowClick: MouseEventHandler<HTMLTableRowElement> = e => {
    if (!href) return
    if (e.button === 1) {
      window.open(href(value), '_blank')
      return
    }
  }

  return (
    <tr
      data-testid={`datatable-row-${hasName(value) ? value.name : ''}`}
      onAuxClick={handleRowClick}
      onClick={() => (onClick ? onClick(value) : href && navigate(href(value)))}
      className={`${classes.tr} ${hasClickEvent ? classes.clickable : ''} ${
        className ?? ''
      }`}
      style={rowStyle}
    >
      {headers.map((header, i) => (
        <Td
          mobileGrow={headers[i].mobileGrow}
          mobileHidden={headers[i].mobileHidden}
          key={i}
        >
          <span className={classes.mobileLabel}>{headers[i]?.label}</span>
          {header.selector(value, { dayjs })}
        </Td>
      ))}
    </tr>
  )
}

type TdProps = {
  mobileHidden?: boolean
  mobileGrow?: boolean
}

const Td = styled.td<TdProps>`
  display: ${props => (props.mobileHidden ? 'none' : 'block')};
  flex-grow: ${props => (props.mobileGrow ? 1 : 0)};
  width: ${props => (props.mobileGrow ? '100%' : 'auto')};
  min-width: 0;
  padding: var(--mantine-spacing-sm) var(--mantine-spacing-md);
  font-size: 0.9rem;
  font-weight: 500;

  @media (min-width: 75em) {
    display: table-cell;
    flex-grow: 0;
    padding: var(--mantine-spacing-md);
    vertical-align: middle;
    width: auto;
  }
`
